import { getDocumentCode } from '@ourbranch/document-utils';
import { getDateStrInPolicyTZ } from '@ourbranch/date-fns-helpers';
import { getMailingHoldDaysForPolicyId } from '@ourbranch/policy-utils';
import { differenceInCalendarDays, parseISO } from 'date-fns';

import { dateFormatter } from 'core/helpers/formatters';

/**
 * The one-line delivery history under a document in the Policy Documents list (#36832):
 * "Mailed 10/01/2026 to insured · Emailed 10/20/2026 to agent (agent@agency.example)".
 *
 * Deliveries come from the documentDeliveries ledger, which every sender of a stored policy
 * document writes. History starts when the ledger shipped, so a document from before then shows
 * nothing rather than a misleading "Not sent".
 */

// The documents the paper-docs cron puts in an envelope on a mutual brand: the policy packets, the
// auto ID cards, and on an endorsement the standalone auto/home/condo/renters declarations (the cron's
// docIdsForEndorsement). Only these earn a "Not sent" marker. A farm declarations file, binder,
// invoice or RCE is never mailed on its own there, so marking it would be wrong, not just noise.
export const MAIL_ELIGIBLE_DOC_CODES = new Set([
  'A_PJAK',
  'A_IDCD',
  'A_DECK',
  'H_PJAK',
  'H_DECK',
  'C_PJAK',
  'C_DECK',
  'R_PJAK',
  'R_DECK',
  'F_PJAK',
  'Z_PJAK',
  'Y_PJAK'
]);

export const NOT_SENT = 'Not sent';

// When the ledger started recording (PR #36839 deploy). A document saved before this has no rows
// whether or not it was mailed, so it is never marked "Not sent"; it shows nothing, or the history
// the Lob backfill recovered for it.
export const DOCUMENT_DELIVERY_LEDGER_SINCE = '2026-10-22T00:00:00.000Z';

const CHANNEL_VERB = { mail: 'Mailed', email: 'Emailed' };

const recipientPhrase = ({ recipientType, recipient }) => {
  if (recipientType === 'insured') {
    return 'insured';
  }
  if (recipientType === 'agent') {
    return recipient ? `agent (${recipient})` : 'agent';
  }
  if (recipientType === 'mortgagee') {
    return recipient ? `mortgagee (${recipient})` : 'mortgagee';
  }
  return recipient || 'recipient';
};

// '1of2' → 'letter 1 of 2'. A packet Lob only takes in several letters has a row per letter.
const letterPhrase = (letterPart) => {
  const [number, total] = String(letterPart).split('of');
  return number && total ? `letter ${number} of ${total}` : null;
};

export const describeDelivery = (delivery) => {
  const verb = CHANNEL_VERB[delivery.channel] || 'Sent';
  const date = delivery.sentAt ? dateFormatter(new Date(delivery.sentAt)) : null;
  const letter = delivery.letterPart ? letterPhrase(delivery.letterPart) : null;
  return [verb, date, `to ${recipientPhrase(delivery)}`, letter && `(${letter})`].filter(Boolean).join(' ');
};

// The paper-docs cron's own split (send-paper-docs-cron helpers.ts): on a run more than five days
// past the term's effective date it treats the policy as endorsed and mails the standalone auto/home/
// condo/renters declarations instead of the packet; within those five days it mails the packet. Farm
// policies always mail their packet, and the auto ID cards go either way.
const NEW_BUSINESS_MAILING_DAYS = 5;

// The policy id segment of a stored key: docs/<account>/customer/<policyId>/<stamp>-<code>.pdf
const policyIdOf = (path) => String(path).split('/')[3] || '';

const isFarmCode = (code) => ['F_', 'Z_', 'Y_'].some((prefix) => code.startsWith(prefix));
const familyOf = (code) => (code === 'A_IDCD' ? 'idcards' : 'packet');
const modifiedMs = (doc) => new Date(doc.lastModified).getTime() || 0;
// Calendar days between two business dates ('yyyy-MM-dd'), whatever the browser's timezone.
const daysBetween = (laterDateStr, earlierDateStr) =>
  differenceInCalendarDays(parseISO(laterDateStr), parseISO(earlierDateStr));

// A declarations file generated this close to the term's first packet belongs to that bind-time
// regeneration, which stores an A_DECK next to the packet.
const SAME_GENERATION_MS = 5 * 60 * 1000;

const newest = (docs) => docs.reduce((best, doc) => (!best || modifiedMs(doc) > modifiedMs(best) ? doc : best), null);

// The paper-docs cron's own name for itself on its ledger rows (PAPER_DOCS_DELIVERY_SOURCE in
// @ourbranch/document-deliveries). The cron only counts its own mailings when it reads the ledger, so a
// staff Lob re-send of one file (a lob-queue-processor row) must not count as the packet mailing here.
const PAPER_DOCS_DELIVERY_SOURCE = 'send-paper-docs-cron';

const wasMailed = (doc) =>
  (doc?.deliveries || []).some(
    (delivery) =>
      delivery.channel === 'mail' && delivery.source === PAPER_DOCS_DELIVERY_SOURCE && delivery.packetComplete !== false
  );

// The packet stored in the same regeneration as this declarations file, if any.
const sameGenerationPacket = (declarationsDoc, packets) =>
  packets.find((packet) => Math.abs(modifiedMs(packet) - modifiedMs(declarationsDoc)) <= SAME_GENERATION_MS);

// The one file of a policy's packet family the cron would mail: past day five of the term, the newest
// endorsement declarations when there is one, otherwise the newest packet. A declarations file is an
// endorsement's when it was generated after the term's first packet (a bind-time one is stored in the
// same regeneration as that packet, and the packet is what mailed). The day-five test uses today, as
// the cron's run date does, so an endorsement generated on day five is mailed, and marked, from day
// six. An endorsement mailed as a packet within the first five days keeps that packet as the family's
// file: the packet carried the dec, so its regenerated declarations file is not "Not sent". Farm
// policies only ever mail the packet. With no effective date to place the term against, the newest
// file of the family stands in.
// Whether the ledger has recorded for the whole time this term could have mailed (the cron may mail
// a term up to the brand's mailing lead before its effective date). Mirrors ledgerCoversTermMailing
// in @ourbranch/document-deliveries: for such a term, or for one whose packet mailing the ledger
// already records, the cron reads the ledger and mails the term's NEWEST PACKET (plus ID cards on
// the first mailing or when cars/drivers changed), never a standalone declarations file; an older
// term with no recorded mailing falls back to the day-five date rule below.
// Calendar-date arithmetic in UTC, as ledgerCoversTermMailing does, so the answer is the same in every
// viewer's timezone.
const ledgerCoversTerm = ({ policyId, effectiveDate }) => {
  const [year, month, day] = String(effectiveDate).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) {
    return false;
  }
  const earliestMailing = new Date(Date.UTC(year, month - 1, day - getMailingHoldDaysForPolicyId(policyId)));
  return earliestMailing.toISOString().slice(0, 10) >= DOCUMENT_DELIVERY_LEDGER_SINCE.slice(0, 10);
};

const packetFamilyCandidate = ({ docs, policyId, effectiveDate, todayStr }) => {
  const packets = docs.filter((doc) => getDocumentCode(doc.path).endsWith('_PJAK'));
  const declarations = docs.filter((doc) => getDocumentCode(doc.path).endsWith('_DECK'));
  if (declarations.length && isFarmCode(getDocumentCode(declarations[0].path))) {
    return newest(packets);
  }
  if (!effectiveDate) {
    return newest(docs);
  }
  // The cron takes the ledger's answer when it covers the term, and also whenever the ledger already
  // records a mailed packet for it (a backfilled mailing on an older term): either way it mails the
  // newest packet. Only a term with neither falls back to the date rule.
  if (ledgerCoversTerm({ policyId, effectiveDate }) || packets.some(wasMailed) || declarations.some(wasMailed)) {
    return newest(packets);
  }
  if (daysBetween(todayStr, effectiveDate) <= NEW_BUSINESS_MAILING_DAYS) {
    return newest(packets);
  }
  const firstPacketMs = packets.length ? Math.min(...packets.map(modifiedMs)) : Number.NEGATIVE_INFINITY;
  const endorsementDec = newest(declarations.filter((doc) => modifiedMs(doc) > firstPacketMs + SAME_GENERATION_MS));
  if (!endorsementDec) {
    return newest(packets);
  }
  const siblingPacket = sameGenerationPacket(endorsementDec, packets);
  return siblingPacket && wasMailed(siblingPacket) ? siblingPacket : endorsementDec;
};

/**
 * Which documents may read "Not sent": per policy, the file the cron would mail in each family (the
 * packet family: the newest packet for a term the ledger covers, or, for an older term under the
 * cron's date rule, the endorsement declarations that superseded it on a non-farm policy; and the
 * newest ID cards). The cron only ever mails the latest eligible file per doc type,
 * so a superseded file, or a declarations file stored next to the packet at new business, was never
 * going to be mailed on its own and must not be marked.
 *
 * The day-five test is made on the policy state's business date (the cron's run-date rule, and the
 * repo's convention for business dates), not the viewer's browser calendar, so two staff in different
 * timezones see the same marker.
 *
 * @param {Array<{path: string, lastModified: string}>} docs the Policy Documents rows
 * @param {object} options `policies` (with `id`, `state` and `effectiveDate`) for the day-five rule;
 *   `now`, the instant to evaluate at (tests)
 * @returns {Set<string>} the paths that may be marked
 */
export const notSentCandidatePaths = (docs, { policies = [], now = new Date() } = {}) => {
  const policyById = new Map((policies || []).map((policy) => [policy?.id, policy]));
  const familyDocs = new Map();
  for (const doc of docs || []) {
    const code = doc?.path ? getDocumentCode(doc.path) : null;
    if (!code || !MAIL_ELIGIBLE_DOC_CODES.has(code)) {
      continue;
    }
    const key = `${policyIdOf(doc.path)}|${familyOf(code)}`;
    familyDocs.set(key, [...(familyDocs.get(key) || []), doc]);
  }
  const candidates = new Set();
  for (const [key, family] of familyDocs) {
    const [policyId, familyName] = key.split('|');
    const policy = policyById.get(policyId);
    const candidate =
      familyName === 'idcards'
        ? newest(family)
        : packetFamilyCandidate({
            docs: family,
            policyId,
            effectiveDate: policy?.effectiveDate,
            todayStr: getDateStrInPolicyTZ(policy?.state, now.toISOString())
          });
    if (candidate) {
      candidates.add(candidate.path);
    }
  }
  return candidates;
};

const isCoveredByLedger = (doc) =>
  Boolean(doc?.lastModified) && new Date(doc.lastModified).toISOString() >= DOCUMENT_DELIVERY_LEDGER_SINCE;

/**
 * @param {object} doc a Policy Documents row (`path`, `lastModified`, `deliveries`)
 * @param {object} options `markNotSent`: whether an unmailed mail-eligible document reads "Not sent".
 *   Mutual brands mail every member; Branch has paperless members, so it never marks. Callers pass
 *   it only for the documents notSentCandidatePaths names, so superseded files are never marked.
 * @returns {string|null} the summary line, or null when there is nothing to say
 */
export const summarizeDeliveries = (doc, { markNotSent = false } = {}) => {
  // Only a query that selected `deliveries` can say anything: other queries fill the same documents
  // store without it, and an absent field must not read as "never sent".
  const hasLedgerData = Array.isArray(doc?.deliveries);
  if (!hasLedgerData) {
    return null;
  }
  if (doc.deliveries.length) {
    return doc.deliveries.map(describeDelivery).join(' · ');
  }
  if (markNotSent && isCoveredByLedger(doc) && doc?.path && MAIL_ELIGIBLE_DOC_CODES.has(getDocumentCode(doc.path))) {
    return NOT_SENT;
  }
  return null;
};
